import { Injectable, Output, EventEmitter, Directive } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { tap, catchError, map, share, mapTo } from 'rxjs/operators';

import { BehaviorSubject, Observable, of, throwError } from 'rxjs';

import { LocalStorageService } from 'angular-web-storage';
import { JwtHelperService } from './jwt-helper.service';

import { environment } from '@env/environment';
import { Clinic, Laboratory } from '../models';

import swal from 'sweetalert2';
import { Router } from '@angular/router';
import { APPOINTMENT_FILTER, CALENDAR_FILTER_PANEL_VISIBILITY_KEY, CALENDAR_SHOW_ALL_APPOINTMENTS_KEY, DEVICE_ID_KEY, PHYSICAL_DEVICE_ID_KEY, THEME_PREFERENCES_KEY } from '../constants/settings.constants';
import { DEVICE_FINGERPRINT_KEY } from '../services/helpers/device-fingerprint.service';
import { shutdown as shutdownIntercom } from '@intercom/messenger-js-sdk';

const TOKEN_KEY = 'access_token';
const REF_TOKEN_KEY = 'refresh_token';

@Directive()
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly _loginProfile = new BehaviorSubject<any>(null);
  public loginProfile$ = this._loginProfile.asObservable();
  gettingLoginProfile: boolean = false;

  user = null;
  loading: any;
  profile: any;
  private readonly patientLock = new BehaviorSubject<any>(false);
  readonly patientFormLocked$ = this.patientLock.asObservable();
  get patientFormLocked(): any { return this.patientLock.value; }
  set patientFormLocked(value: any) { this.patientLock.next(value); }
  private readonly appLock = new BehaviorSubject(false);
  readonly appLocked$ = this.appLock.asObservable();
  private readonly kioskMode = new BehaviorSubject(false);
  readonly patientEntryKioskMode$ = this.kioskMode.asObservable();
  get patientEntryKioskMode(): boolean { return this.kioskMode.value; }
  set patientEntryKioskMode(value: boolean) { this.kioskMode.next(!!value); }
  team: any;
  private sessionExpiredLogoutPending = false;

  authenticationState = new BehaviorSubject(false);

  @Output() labChanged: EventEmitter<boolean> = new EventEmitter();
  @Output() clinicChanged: EventEmitter<boolean> = new EventEmitter();

  constructor(
    private http: HttpClient,
    private helper: JwtHelperService,
    private storage: LocalStorageService,
    private router: Router
  ) {
    this.checkToken();
    this.setUser(this.storage.get(TOKEN_KEY));
    this.checkPtFormLockStatus();
    this.appLock.next(!!this.isAppLocked());
  }

  checkToken() {
    let token = this.storage.get(TOKEN_KEY);
    if (token) {
      let decoded = this.helper.decodeToken(token);
      let isExpired = this.helper.isTokenExpired(token);
      this.authenticationState.next(true);
      this.user = decoded;
      if (isExpired) {
        this.refresh(true);
      }
    } else {
      this.authenticationState.next(false);
    }
  }

  login(credentials, noSetToken?: boolean) {
    if (!credentials.password || !credentials.email) {
      return;
    }
    return this.http.post(`${this.apiUrl}/auth/login`, credentials).pipe(
      tap((data) => {
        if (data && data[TOKEN_KEY]) {
          if (noSetToken) {
          } else {
            this.setUserTokens(data);
          }
        }
      })
    );
  }

  refreshToken() {
    // let token = this.storage.get(REF_TOKEN_KEY);
    const token = this.storage.get(REF_TOKEN_KEY);
    let data: any = {
      token: token
    };
    const clinic_id = this.getUserData('clinic_id');
    if (clinic_id) {
      data.clinic_id = clinic_id;
    }
    if (this.patientEntryKioskMode || (this.user && this.user.data && this.user.data.kiosk)) {
      data.kiosk = 1;
    }

    return this.http.post(`${this.apiUrl}/auth/refresh`, data).pipe(
      tap((data) => {
        if (data && data[TOKEN_KEY]) {
          this.setUserTokens(data);
        }
      }),
      catchError((error) => {
        this.logout(true);
        return throwError(error);
      })
    );
  }

  refresh(showAlert?: boolean) {
    return this.refreshToken().subscribe(
      (data) => {
        return this.authenticationState.next(true);
      },
      (error) => {
        return this.authenticationState.next(false);
      }
    );
  }

  lockApp() {
    this.storage.set('SWACT_X', 1);
    this.appLock.next(true);
  }

  unlockApp() {
    this.storage.remove('SWACT_X');
    this.appLock.next(false);
  }

  isAppLocked() {
    return this.storage.get('SWACT_X');
  }

  getTeam() {
    const token = this.storage.get(TOKEN_KEY);
    const clinic_id = this.getUserData('clinic_id');
    if (!clinic_id) {
      return;
    }
    return this.http.get(`${this.apiUrl}/auth/team`);
    // return this.http.post(`${this.url}/auth/team`, { token: token, clinic_id: clinic_id });
  }

  setUserTokens(data: any) {
    this.setUser(data[TOKEN_KEY]);
    this.storage.set(TOKEN_KEY, data[TOKEN_KEY]);
    this.storage.set(REF_TOKEN_KEY, data[REF_TOKEN_KEY]);
  }

  getToken() {
    return this.storage.get(TOKEN_KEY);
  }

  logout(showAlert?: boolean) {
    if (showAlert) {
      if (this.sessionExpiredLogoutPending) {
        return;
      }
      this.sessionExpiredLogoutPending = true;
      this.showLoginExpiredAlert().then(() => {
        this.doLogout();
      });
      return;
    } else {
      //   this.storage.remove('account_logo');
      //   this.storage.remove('digital_signature');
      this.doLogout();
      return this.authenticationState.next(false);
    }
  }

  private showLoginExpiredAlert(): Promise<any> {
    return swal({
      type: 'warning',
      title: 'Login expired. Please login again.'
    });
  }

  private doLogout() {
    this.patientFormLocked = false;
    try {
      shutdownIntercom();
    } catch (_) {}
    this.clearLocalStorageExcept([DEVICE_ID_KEY, APPOINTMENT_FILTER, DEVICE_FINGERPRINT_KEY, THEME_PREFERENCES_KEY, CALENDAR_SHOW_ALL_APPOINTMENTS_KEY, CALENDAR_FILTER_PANEL_VISIBILITY_KEY]);
    this.router.navigate(['auth/login']);
    this.unlockApp();
    setTimeout(() => {
      window.location.reload();
    }, 1);
  }

  isAuthenticated() {
    // this.checkToken();
    return this.authenticationState.value;
  }

  startClinic(clinic: Clinic, kioskMode?: boolean): Observable<boolean> {
    const token = this.storage.get(TOKEN_KEY);
    let data: any = {
      token: token,
      clinic_id: clinic.id
    };
    if (kioskMode) {
      data.kiosk = 1;
    }
    return this.http.post(`${this.apiUrl}/auth/clinics`, data).pipe(
      tap((data) => {
        if (data && data[TOKEN_KEY]) {
          this.setUserTokens(data);
          this.clinicChanged.emit(this.getUserData('clinic_id'));
        }
      }),
      map((data) => true),
      catchError((error) => {
        swal(error.error, '', 'error');
        return throwError(error);
      })
    );
  }

  startLaboratory(lab: Laboratory): Observable<boolean> {
    let token = this.storage.get(TOKEN_KEY);
    return this.http.post(`${this.apiUrl}/auth/laboratories`, { token: token, laboratory_id: lab.id }).pipe(
      tap((data) => {
        if (data && data[TOKEN_KEY]) {
          this.storage.set(TOKEN_KEY, data[TOKEN_KEY]);
          this.storage.set(REF_TOKEN_KEY, data[REF_TOKEN_KEY]);
          this.setUser(data[TOKEN_KEY]);
          this.labChanged.emit(this.getUserData('laboratory_id'));
        }
      }),
      map((data) => true),
      catchError((error) => {
        swal(error.error, '', 'error');
        return throwError(error);
      })
    );
  }

  isAdmin() {
    return this.user.is_admin;
  }

  isAssociate() {
    if (parseInt(this.user.data.login_type) < 16) {
      return false;
    }
    return true;
  }

  isStaff() {
    if (parseInt(this.user.data.login_type) < 16) {
      return true;
    }
    return false;
  }

  private setUser(token) {
    if (!token) return;
    this.user = this.helper.decodeToken(token);
    if (parseInt(this.user.data.is_admin) == 1) {
      this.user.is_admin = true;
    } else {
      this.user.is_admin = false;
    }
  }

  getLoginProfile(reload?: boolean) {
    if (this.gettingLoginProfile || (this.profile && this.profile.id && !reload)) {
      return;
    }

    this.gettingLoginProfile = true;
    this.profile = null;
    this._loginProfile.next(null);

    let profileUrl: string = `${this.apiUrl}/accounts/staff/${this.getUserData('id')}`;
    if (parseInt(this.getUserData('login_type')) == 16) {
      profileUrl = `${this.apiUrl}/accounts/associates/${this.getUserData('id')}`;
    }
    this.http.get(profileUrl).subscribe(
      (data: any) => {
        this.gettingLoginProfile = false;
        this.profile = data;
        this._loginProfile.next(data);
      },
      (_) => {
        this.gettingLoginProfile = false;
      }
    );
  }

  patchLoginProfile(profilePatch: any) {
    const profile = Object.assign({}, this.profile || {}, profilePatch || {});
    this.profile = profile;
    this._loginProfile.next(profile);
  }

  updateProfilePhoto(file: Blob, filename?: string): Observable<any> {
    const data = new FormData();
    data.append('photo', file, filename || this.getProfilePhotoFilename(file));
    const previousPhoto = this.profile && this.profile.profilePhoto;

    return this.http.post(`${this.apiUrl}/accounts/profile-photo`, data).pipe(
      tap((profile: any) => {
        if (previousPhoto && previousPhoto !== profile?.profilePhoto) {
          this.storage.remove(previousPhoto);
          localStorage.removeItem(previousPhoto);
          localStorage.removeItem('uri_' + previousPhoto);
        }
        this.patchLoginProfile(profile);
      })
    );
  }

  private getProfilePhotoFilename(file: Blob): string {
    if (file instanceof File && file.name && file.name !== 'blob') {
      return file.name;
    }

    return file.type === 'image/png' ? 'profile-photo.png' : 'profile-photo.jpg';
  }

  updatePassword(data: any) {
    return this.http.post(`${this.apiUrl}/auth/password`, data).pipe(map((data) => true));
  }

  forgotPassword(email: string) {
    let url = window.location.origin + '/auth/recover';
    return this.http.post(`${this.apiUrl}/auth/forgot-password`, { email: email, url: url });
  }

  recoverPassword(data: any) {
    return this.http.post(`${this.apiUrl}/auth/recover-password`, data);
  }

  getUserData(key: string) {
    if (this.user) {
      return this.user.data[key];
    }
    return null;
  }

  getMyClinics() {
    return this.http.get(`${this.apiUrl}/auth/clinics`);
  }

  getMyLaboratories() {
    return this.http.get(`${this.apiUrl}/auth/laboratories`);
  }

  register(data) {
    return this.http.post(`${environment.baseApiUrl}/register`, data);
  }

  // Email verification methods
  sendVerificationCode(email: string) {
    return this.http.post(`${environment.baseApiUrl}/email-verification/send`, { email });
  }

  verifyEmailCode(email: string, verificationCode: string) {
    return this.http.post(`${environment.baseApiUrl}/email-verification/verify`, {
      email,
      verification_code: verificationCode
    });
  }

  resendVerificationCode(email: string) {
    return this.http.post(`${environment.baseApiUrl}/email-verification/resend`, { email });
  }


  proceedWithPayment(registrationId: number, mobileBankingType: 'hitpay' | 'xendit' = 'hitpay') {
    return this.http.post(`${environment.baseApiUrl}/proceed-payment`, {
      registration_id: registrationId,
      mobile_banking_type: mobileBankingType,
    });
  }

  // Signup token methods
  getRetainedData(token: string) {
    return this.http.get(`${environment.baseApiUrl}/signup/retained-data/${token}`);
  }

  proceedPaymentWithToken(token: string, mobileBankingType: 'hitpay' | 'xendit' = 'hitpay') {
    return this.http.post(`${environment.baseApiUrl}/signup/proceed-payment-with-token`, {
      token,
      mobile_banking_type: mobileBankingType,
    });
  }

  getPlans() {
    return this.http.get(`${this.apiUrl}/plans`);
  }

  lockPatientForm(url: string, lockKey: string) {
    const key = this.digestMessage(lockKey);
    const lockData: any = { url: url, key: key, isLockedForm: true };
    this.patientFormLocked = lockData;
    this.storage.set('PTXFRM', lockData);
  }

  unlockPatientForm(lockKey) {
    const key = this.digestMessage(lockKey);
    if (key == this.patientFormLocked.key) {
      this.patientFormLocked = false;
      this.storage.remove('PTXFRM');
      return true;
    } else {
      return false;
    }
  }

  checkPtFormLockStatus() {
    const ptStat = this.storage.get('PTXFRM');
    if (ptStat) {
      this.patientFormLocked = ptStat;
    }
    if (this.user && this.user.data && this.user.data.kiosk) {
      this.patientEntryKioskMode = true;
    }
    // console.log(this.user);
  }

  digestMessage(s) {
    let a = 1,
      c = 0,
      h,
      o;
    if (s) {
      a = 0;
      for (h = s.length - 1; h >= 0; h--) {
        o = s.charCodeAt(h);
        a = ((a << 6) & 268435455) + o + (o << 14);
        c = a & 266338304;
        a = c !== 0 ? a ^ (c >> 21) : a;
      }
    }
    return String(a);
  }

  updateEmailData(data: any): Observable<any> {
    return this.http.put(`${this.apiUrl}/auth/update-email`, data).pipe(map((data) => true));
  }

  clearLocalStorageExcept(keysToKeep: string[]) {
    let keysToRemove = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!keysToKeep.includes(key)) {
        keysToRemove.push(key);
      }
    }

    for (const key of keysToRemove) {
      localStorage.removeItem(key);
    }
  }

  /**
   * Handle SSO token exchange
   * Validates token, stores auth data, and returns success status
   * @param token - The SSO token from URL
   * @returns Promise<boolean> - true if successful, false otherwise
   */
  async handleSSOTokenExchange(token: string): Promise<boolean> {
    try {
      const userData: any = await this.http.post(`${this.apiUrl}/auth/validate-sso-token`, { token }).toPromise();

      if (!userData || !userData.access_token) {
        console.error('SSO: Invalid response from validation endpoint');
        return false;
      }

      this.setUserTokens(userData);
      // Store device ID if provided
      if (userData.deviceId) {
        localStorage.setItem(DEVICE_ID_KEY, userData.deviceId);
      }
      this.authenticationState.next(true);
      // Load user profile
      await this.getLoginProfile(true);
      return true;

    } catch (error) {
      console.error('SSO: Failed to validate token:', error);
      return false;
    }
  }

  /**
   * Update device label from access request (no JWT required, uses token instead)
   * @param deviceFingerprint Device fingerprint to update
   * @param token Secure token from error response
   * @param deviceName New label for the device
   * @returns Observable with response
   */
  updateDeviceLabelFromAccessRequest(deviceFingerprint: string, token: string, deviceName: string): Observable<any> {
    return this.http.put(`${this.apiUrl}/auth/device-access-request/${deviceFingerprint}/label`, { token, deviceName });
  }

  get apiUrl() {
    return environment.apiUrlV2;
  }
}
